import type {DeepPartial} from '@/global';
import type {RGBA} from '@Utils/color.util';
import type {FillObject} from '@PosterWhiteboard/classes/fill.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.types';
import type {ItemBorderObject} from '@PosterWhiteboard/classes/item-border.types';
import {BorderType} from '@PosterWhiteboard/classes/item-border.types';
import type {QRCodeItemObject} from '@PosterWhiteboard/items/qr-code-item.class';
import type {QRCodeFileExtension, QRPayloadField, QRPayloadIssue, QRPayloadResult, QRRenderConfig} from '@Libraries/qr-code/qr-code.types';
import {
  QR_PAYLOAD_MAX_BYTES,
  QR_STYLE_PREVIEW_DIRECTORY,
  QR_STYLE_PREVIEW_EXTENSION,
  QRPayloadIssueCode,
  QRPayloadType,
  QRPayloadVariant,
  QRType,
  QRWifiSecurity,
} from '@Libraries/qr-code/qr-code.types';
import {getUtf8ByteLength} from '@Libraries/qr-code/qr-payload-encoding.library';
import {getReadBucket, repoURL} from '@Libraries/s3-library';
import {
  buildAppStorePayload,
  buildCalendarEventPayload,
  buildEmailPayload,
  buildPhonePayload,
  buildSmsPayload,
  buildSocialPayload,
  buildTextPayload,
  buildVCardPayload,
  buildWebUrlPayload,
  buildWifiPayload,
} from '@Libraries/qr-code/qr-payload.library';
import type {QRSocialPlatform} from '@Libraries/qr-code/qr-social-platform.library';
import {getQRPlatformLogoReference, getQRSocialPlatform, hasQRSocialPlatformIcon} from '@Libraries/qr-code/qr-social-platform.library';
import {addOutlineToQRCode, hasQROutline, renderQRCodeBlob} from '@Libraries/qr-code/qr-render.library';
import type {
  QRCodeStyle,
  QRDesignTile,
  QRDesignTileSets,
  QRGeneratorLogo,
  QRGeneratorState,
  QRPreviewStyle,
  QRRenderInputs,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {
  getInitialQRGeneratorState,
  LOGO_BEARING_TYPES,
  QR_CLIPBOARD_IMAGE_MIME_TYPE,
  QR_LOGO_ACCEPTED_FILE_TYPES,
  QR_LOGO_MAX_FILE_BYTES,
  QR_LOGO_SIZE_RATIO,
  QR_SAMPLE_PAYLOAD,
  QR_TOOL_TYPES,
  QRCopyState,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {assertNever} from '@Utils/assert-never.util';

const toSolidFill = (color: RGBA): FillObject => {
  return {fillType: FillTypes.SOLID, fillColor: [color]};
};

const LOGO_FILE_TYPE_SEPARATOR = ',';
const QR_ASSETS_FOLDER = 'assets';
const BYTES_PER_MEGABYTE = 1024 * 1024;

export const getQRLogoRejection = (file: File): string | null => {
  if (!QR_LOGO_ACCEPTED_FILE_TYPES.split(LOGO_FILE_TYPE_SEPARATOR).includes(file.type)) {
    return window.i18next.t('pmwjs_qr_generator_logo_wrong_type');
  }
  if (file.size > QR_LOGO_MAX_FILE_BYTES) {
    return window.i18next.t('pmwjs_qr_generator_logo_too_large', {megabytes: QR_LOGO_MAX_FILE_BYTES / BYTES_PER_MEGABYTE});
  }
  return null;
};

export const getPreviousCodeIndex = (currentIndex: number, codeCount: number): number => {
  return (currentIndex - 1 + codeCount) % codeCount;
};

export const getNextCodeIndex = (currentIndex: number, codeCount: number): number => {
  return (currentIndex + 1) % codeCount;
};

export const getVisibleCodeIndex = (requestedIndex: number, codeCount: number): number => {
  return requestedIndex < codeCount ? requestedIndex : 0;
};

export const isShowingSampleQRCode = (state: QRGeneratorState, activePayload: string): boolean => {
  const untouchedState = {...getInitialQRGeneratorState(state.calendarEventInput.generatedAt), activeType: state.activeType};
  return getResultForActiveType(untouchedState).codes[0].payload === activePayload;
};

export const getResultForActiveType = (state: QRGeneratorState): QRPayloadResult => {
  switch (state.activeType) {
    case QRPayloadType.URL:
      return buildWebUrlPayload(state.urlInput);
    case QRPayloadType.SOCIAL:
      return buildSocialPayload(state.socialInput);
    case QRPayloadType.TEXT:
      return buildTextPayload(state.textInput);
    case QRPayloadType.EMAIL:
      return buildEmailPayload(state.emailInput);
    case QRPayloadType.PHONE:
      return buildPhonePayload(state.phoneInput);
    case QRPayloadType.SMS:
      return buildSmsPayload(state.smsInput);
    case QRPayloadType.WIFI:
      return buildWifiPayload(state.wifiInput);
    case QRPayloadType.VCARD:
      return buildVCardPayload(state.vCardInput);
    case QRPayloadType.CALENDAR_EVENT:
      return buildCalendarEventPayload(state.calendarEventInput);
    case QRPayloadType.PAYMENT_LINK:
      return buildWebUrlPayload(state.paymentLinkInput);
    case QRPayloadType.APP_STORE:
      return buildAppStorePayload(state.appStoreInput);
    case QRPayloadType.MULTIMEDIA:
      return buildWebUrlPayload(state.multimediaInput);
    default:
      return assertNever(state.activeType);
  }
};

export const getActiveLogoPlatform = (activeType: QRPayloadType, payload: string): QRSocialPlatform | null => {
  return LOGO_BEARING_TYPES.includes(activeType) ? getQRSocialPlatform(payload) : null;
};

export const getHandoffLogoReference = (logo: QRGeneratorLogo | null, socialPlatform: QRSocialPlatform | null): string | null => {
  if (logo !== null || socialPlatform === null || !hasQRSocialPlatformIcon(socialPlatform)) {
    return null;
  }
  return getQRPlatformLogoReference(socialPlatform);
};

export const isQRStyleOverrideApplied = (style: QRCodeStyle, override: Partial<QRCodeStyle>): boolean => {
  return (
    (override.dotStyle === undefined || override.dotStyle === style.dotStyle) &&
    (override.eyeFrameStyle === undefined || override.eyeFrameStyle === style.eyeFrameStyle) &&
    (override.eyeBallStyle === undefined || override.eyeBallStyle === style.eyeBallStyle)
  );
};

export const getQRStylePreviewUrl = (previewFileName: string): string => {
  return repoURL(`${QR_ASSETS_FOLDER}/${QR_STYLE_PREVIEW_DIRECTORY}${previewFileName}${QR_STYLE_PREVIEW_EXTENSION}`, getReadBucket(true));
};

const isDesignTile = (value: unknown): value is QRDesignTile => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const tile: {name?: unknown; designHashedId?: unknown; thumbnailUrl?: unknown} = value;
  return typeof tile.name === 'string' && typeof tile.designHashedId === 'string' && typeof tile.thumbnailUrl === 'string';
};

export const getDesignTileAspectRatio = (tile: QRDesignTile): string | null => {
  if (typeof tile.width !== 'number' || typeof tile.height !== 'number' || tile.width <= 0 || tile.height <= 0) {
    return null;
  }
  return `${tile.width} / ${tile.height}`;
};

const isTileSetRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const OFFERED_TOOL_TYPE_VALUES: ReadonlySet<string> = new Set<string>(QR_TOOL_TYPES);

const isOfferedToolType = (serialisedType: string): serialisedType is QRPayloadType => {
  return OFFERED_TOOL_TYPE_VALUES.has(serialisedType);
};

export const parseDesignTileSets = (serialisedTileSets: string | undefined): QRDesignTileSets => {
  if (serialisedTileSets === undefined || serialisedTileSets === '') {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(serialisedTileSets);
    if (!isTileSetRecord(parsed)) {
      return {};
    }
    const tileSets: QRDesignTileSets = {};
    Object.entries(parsed).forEach(([codeType, tileSet]: [string, unknown]): void => {
      if (!isOfferedToolType(codeType) || !Array.isArray(tileSet)) {
        return;
      }
      const tiles = tileSet.filter(isDesignTile);
      if (tiles.length > 0) {
        tileSets[codeType] = tiles;
      }
    });
    return tileSets;
  } catch (error) {
    window.PMW.log('Could not read a QR tool design list', error);
    return {};
  }
};

export const getDesignTilesForType = (tileSets: QRDesignTileSets, activeType: QRPayloadType): QRDesignTile[] => {
  return tileSets[activeType] ?? [];
};

export const getDefaultActiveType = (serialisedType: string | undefined): QRPayloadType => {
  if (serialisedType === undefined || !isOfferedToolType(serialisedType)) {
    return QRPayloadType.URL;
  }
  return serialisedType;
};

export const getPreviewPayload = (payload: string): string => {
  return payload.trim() === '' ? QR_SAMPLE_PAYLOAD : payload;
};

const getRenderConfig = (inputs: QRRenderInputs): QRRenderConfig => {
  return {
    data: inputs.payload,
    sizePx: inputs.sizePx,
    marginPercent: inputs.marginPercent,
    backgroundCornerRadiusPercent: inputs.backgroundCornerRadiusPercent,
    dotStyle: inputs.dotStyle,
    eyeFrameStyle: inputs.eyeFrameStyle,
    eyeBallStyle: inputs.eyeBallStyle,
    foregroundFill: toSolidFill(inputs.foregroundColor),
    backgroundFill: toSolidFill(inputs.backgroundColor),
    logoDataUrl: inputs.logoDataUrl,
    logoSizeRatio: QR_LOGO_SIZE_RATIO,
  };
};

export const renderStyledQRCodeBlob = async (inputs: QRRenderInputs, extension: QRCodeFileExtension): Promise<Blob> => {
  const blob = await renderQRCodeBlob(getRenderConfig(inputs), extension);
  if (!hasQROutline(inputs.outlineThickness)) {
    return blob;
  }
  return addOutlineToQRCode(
    blob,
    extension,
    {thickness: inputs.outlineThickness, color: inputs.outlineColor, backgroundCornerRadiusPercent: inputs.backgroundCornerRadiusPercent},
    inputs.sizePx
  );
};

export const copyQRCodeToClipboard = async (inputs: QRRenderInputs): Promise<void> => {
  if (typeof ClipboardItem === 'undefined' || navigator.clipboard?.write === undefined) {
    throw new Error('This browser cannot put an image on the clipboard');
  }
  const pngBlob = renderStyledQRCodeBlob(inputs, 'png');
  await navigator.clipboard.write([new ClipboardItem({[QR_CLIPBOARD_IMAGE_MIME_TYPE]: pngBlob})]);
};

export const getCopyFeedbackText = (copyState: QRCopyState): string | null => {
  switch (copyState) {
    case QRCopyState.IDLE:
      return null;
    case QRCopyState.COPIED:
      return window.i18next.t('pmwjs_qr_generator_copied');
    case QRCopyState.FAILED:
      return window.i18next.t('pmwjs_qr_generator_copy_failed');
    default:
      return assertNever(copyState);
  }
};

export const getCopyIconName = (copyState: QRCopyState): string => {
  switch (copyState) {
    case QRCopyState.IDLE:
      return 'icon-copy';
    case QRCopyState.COPIED:
      return 'icon-check';
    case QRCopyState.FAILED:
      return 'icon-copy';
    default:
      return assertNever(copyState);
  }
};

/** Silent until the payload is full: the cap is the one thing about typed text the visitor cannot work out from the field itself. */
export const getTextContentHelpText = (text: string): string | undefined => {
  if (getUtf8ByteLength(text) >= QR_PAYLOAD_MAX_BYTES) {
    return window.i18next.t('pmwjs_qr_generator_text_at_cap');
  }
  return undefined;
};

export const getQRCodeVariantLabel = (variant: QRPayloadVariant): string | null => {
  switch (variant) {
    case QRPayloadVariant.APP_STORE:
      return window.i18next.t('pmwjs_qr_generator_variant_app_store');
    case QRPayloadVariant.PLAY_STORE:
      return window.i18next.t('pmwjs_qr_generator_variant_play_store');
    case QRPayloadVariant.DEFAULT:
      return null;
    default:
      return assertNever(variant);
  }
};

const QR_ISSUE_LABEL_KEYS: Record<QRPayloadIssueCode, string> = {
  [QRPayloadIssueCode.REQUIRED]: 'pmwjs_qr_generator_required',
  [QRPayloadIssueCode.INVALID_URL]: 'pmwjs_qr_generator_invalid_url',
  [QRPayloadIssueCode.URL_HAS_WHITESPACE]: 'pmwjs_qr_generator_url_has_whitespace',
  [QRPayloadIssueCode.URL_UNSUPPORTED_SCHEME]: 'pmwjs_qr_generator_url_unsupported_scheme',
  [QRPayloadIssueCode.URL_HAS_CREDENTIALS]: 'pmwjs_qr_generator_url_has_credentials',
  [QRPayloadIssueCode.INVALID_EMAIL]: 'pmwjs_qr_generator_invalid_email',
  [QRPayloadIssueCode.PHONE_TOO_SHORT]: 'pmwjs_qr_generator_phone_too_short',
  [QRPayloadIssueCode.INVALID_DATE]: 'pmwjs_qr_generator_invalid_date',
  [QRPayloadIssueCode.DESCRIPTION_TOO_LONG]: 'pmwjs_qr_generator_description_too_long',
  [QRPayloadIssueCode.END_BEFORE_START]: 'pmwjs_qr_generator_end_before_start',
  [QRPayloadIssueCode.OVER_BYTE_CAP]: 'pmwjs_qr_generator_over_byte_cap',
};

const getIssueTranslation = (code: QRPayloadIssueCode): string => {
  return window.i18next.t(QR_ISSUE_LABEL_KEYS[code]);
};

export const getQRFieldIssueText = (issues: QRPayloadIssue[], field: QRPayloadField): string | undefined => {
  const fieldIssue = issues.find((payloadIssue) => {
    return payloadIssue.field === field && payloadIssue.code !== QRPayloadIssueCode.REQUIRED;
  });
  return fieldIssue === undefined ? undefined : getIssueTranslation(fieldIssue.code);
};

export const getPayloadLevelIssueText = (issues: QRPayloadIssue[]): string | null => {
  const payloadIssue = issues.find((issue) => {
    return issue.field === null;
  });
  return payloadIssue === undefined ? null : getIssueTranslation(payloadIssue.code);
};

const QR_TYPE_TAB_LABEL_KEYS: Record<QRPayloadType, string> = {
  [QRPayloadType.URL]: 'pmwjs_qr_generator_type_url',
  [QRPayloadType.SOCIAL]: 'pmwjs_qr_generator_type_social',
  [QRPayloadType.TEXT]: 'pmwjs_qr_generator_type_text',
  [QRPayloadType.EMAIL]: 'pmwjs_qr_generator_type_email',
  [QRPayloadType.PHONE]: 'pmwjs_qr_generator_type_phone',
  [QRPayloadType.SMS]: 'pmwjs_qr_generator_type_sms',
  [QRPayloadType.WIFI]: 'pmwjs_qr_generator_type_wifi',
  [QRPayloadType.VCARD]: 'pmwjs_qr_generator_type_vcard',
  [QRPayloadType.CALENDAR_EVENT]: 'pmwjs_qr_generator_type_calendar_event',
  [QRPayloadType.PAYMENT_LINK]: 'pmwjs_qr_generator_type_payment_link',
  [QRPayloadType.APP_STORE]: 'pmwjs_qr_generator_type_app_store',
  [QRPayloadType.MULTIMEDIA]: 'pmwjs_qr_generator_type_multimedia',
};

export const getTypeTabLabel = (type: QRPayloadType): string => {
  return window.i18next.t(QR_TYPE_TAB_LABEL_KEYS[type]);
};

export const getWifiSecurityLabel = (security: QRWifiSecurity): string => {
  switch (security) {
    case QRWifiSecurity.WPA:
      return window.i18next.t('pmwjs_qr_generator_wifi_security_wpa');
    case QRWifiSecurity.WEP:
      return window.i18next.t('pmwjs_qr_generator_wifi_security_wep');
    case QRWifiSecurity.NONE:
      return window.i18next.t('pmwjs_qr_generator_wifi_security_none');
    default:
      return assertNever(security);
  }
};

const PLATFORM_LOGO_FILE_EXTENSION = 'png';

export const getQRHandoffItemObject = (payload: string, style: QRPreviewStyle, logoReference: string | null): DeepPartial<QRCodeItemObject> => {
  return {
    message: payload,
    qrType: QRType.LINK,
    foregroundFill: toSolidFill(style.foregroundColor),
    backgroundFill: toSolidFill(style.backgroundColor),
    dotStyle: style.dotStyle,
    eyeFrameStyle: style.eyeFrameStyle,
    eyeBallStyle: style.eyeBallStyle,
    margin: style.marginPercent,
    backgroundCornerRadius: style.backgroundCornerRadiusPercent,
    logoHashedFilename: logoReference,
    logoFileExtension: logoReference === null ? null : PLATFORM_LOGO_FILE_EXTENSION,
    logoSizeRatio: QR_LOGO_SIZE_RATIO,
    border: getHandoffBorder(style),
  };
};

const getHandoffBorder = (style: QRPreviewStyle): DeepPartial<ItemBorderObject> => {
  return {
    solidBorderType: hasQROutline(style.outlineThickness) ? BorderType.RECTANGLE_BORDER : BorderType.NONE,
    solidBorderColor: style.outlineColor,
    solidBorderThickness: style.outlineThickness,
  };
};
